import { useFormatCurrency } from "../../hooks";
import { useEffect, useRef, useState, Fragment, useMemo } from "react";
import { useDebounce } from "react-use";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils";
import { axios, getSdkURL } from "../../services";
import { useElementContext, useServiceContext } from "../../contexts";

const ProductList = ({ products, selectedSkus, setSelectedSkus }) => {
  const {
    CommonModule: { DraftOrderProductCard },
  } = useElementContext();
  return products.map((product, index) => {
    return (
      <DraftOrderProductCard
        key={index}
        product={product}
        selectedSkus={selectedSkus}
        setSelectedSkus={setSelectedSkus}
      />
    );
  });
};

const AddProductsTab = ({ orderID, getOrderInfo }) => {
  const [products, setProducts] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isAddingProduct, setAddingProduct] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAllFetched, setAllFetched] = useState(false);

  const [searchstring, setSearchstring] = useState("");
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [formatCurrency] = useFormatCurrency({});
  const loadMoreItemRef = useRef();
  const observer = useRef();
  const { ProductService } = useServiceContext();
  const productService = useMemo(() => new ProductService(), [ProductService]);
  const {
    CommonModule: { SelectedSkuConfiguration },
  } = useElementContext();

  useEffect(() => {
    if (isFetching || isAllFetched || window.innerWidth < 768) return;
    const handleObserver = (entities) => {
      const target = entities[0];
      if (target.isIntersecting) {
        setCurrentPage((prevPage) => prevPage + 1); // Load next page of products
      }
    };
    // Initialize IntersectionObserver when component mounts
    observer.current = new IntersectionObserver(handleObserver, {
      threshold: 0.5,
    });

    // Attach observer to product container element
    if (loadMoreItemRef.current) {
      observer.current.observe(loadMoreItemRef.current);
    }

    return () => {
      // Remove observer when component unmounts
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [isAllFetched, isFetching]);

  useDebounce(
    () => {
      const source = axios.CancelToken.source();
      setIsFetching(true);

      productService
        .search({ searchTerm: searchstring, pageFrom: currentPage + 1, pageSize: 20 }, "product", source)
        .then((data) => {
          if ((data.products || []).length > 0) {
            setProducts((prevProduct) =>
              prevProduct.length === 0 ? data.products : [...prevProduct, ...data.products],
            );
          } else {
            setAllFetched(true);
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    },
    800,
    [currentPage, searchstring],
  );

  const renderProductRow = ({ modifiers, skus, handleSelectedSkuRemove, updateSelectSkuQuantity }) => {
    return skus.map((sku, index) => (
      <tr key={index} className="row align-items-center pb-2 border-bottom">
        <td className="col-1">
          <i
            className="bi bi-trash3 text-primary"
            style={{ cursor: isAddingProduct ? "" : "pointer" }}
            onClick={(e) => {
              if (!isAddingProduct && window.confirm("Do you want to remove this item ?")) {
                handleSelectedSkuRemove(sku);
              }
            }}
          ></i>
        </td>
        <td className="col-3">
          {sku.skuCode}
          {modifiers?.length && sku.skuConfiguration && (
            <SelectedSkuConfiguration modifiers={modifiers} skuConfiguration={sku.skuConfiguration} />
          )}
        </td>
        <td className="col-3">{formatCurrency(sku.price)}</td>
        <td className="col-3">
          <input
            required
            min={1}
            type="number"
            value={sku.quantity}
            onChange={(e) =>
              updateSelectSkuQuantity(
                skus.map((item, subIndex) => {
                  if (index === subIndex) {
                    item.quantity = e.target.value ? parseInt(e.target.value) : "";
                  }
                  return item;
                }),
              )
            }
            className="form-control"
            name="qty"
          />
        </td>
        <td className="col-2">{formatCurrency(sku.price * sku.quantity)}</td>
      </tr>
    ));
  };

  const handleSelectedSkuRemove = (productName, sku) => {
    setSelectedSkus((selectedSkus) =>
      selectedSkus.reduce((acc, item) => {
        if (item.productName === productName) {
          item.skus = item.skus.filter((item) => {
            return item["skuID"] !== sku.skuID;
          });
        }
        if (item.skus.length) acc.push(item);
        return acc;
      }),
    );
  };

  const updateSelectSkuQuantity = (productName, skuList) => {
    setSelectedSkus((selectedSkus) =>
      selectedSkus.map((item) => {
        if (item.productName === productName) {
          item.skus = skuList;
        }
        return item;
      }),
    );
  };

  const addOrderItems = () => {
    if (isAddingProduct) return;
    setAddingProduct(true);
    getOrderInfo({ makeRequest: false });

    const skus = [];
    selectedSkus.forEach((product) => {
      product.skus.forEach((sku) => {
        skus.push({
          skuID: sku.skuID,
          quantity: sku.quantity,
          ...(sku.skuConfiguration ? { skuConfiguration: JSON.stringify(sku.skuConfiguration) } : {}),
        });
      });
    });

    axios({
      method: "POST",
      url: `${getSdkURL()}api/scope/addOrderItemsV2`,
      data: {
        orderID,
        skus,
      },
    })
      .then((response) => {
        if (response?.status === 200 && response?.data?.failureActions.length === 0) {
          setSelectedSkus([]);
          getOrderInfo();
          toast.success("Items added successfully");
        } else {
          toast.error(getErrorMessage(response?.data?.failureActions));
          getOrderInfo({ isFetching: false, makeRequest: false, isLoaded: true });
        }
      })
      .finally(() => {
        setAddingProduct(false);
      });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        addOrderItems();
      }}
    >
      <div className="row justify-content-between py-2 my-2">
        <div className="col-12 col-sm-6 col-xl-4">
          <div className="input-group input-group-lg rounded-pill">
            <input
              className="form-control appended-form-control rounded-pill"
              type="text"
              placeholder="Search products to add"
              required=""
              onChange={(e) => {
                setSearchstring(e.target.value);
                setProducts([]);
                setCurrentPage(0);
                setAllFetched(false);
                setIsFetching(true);
                observer.current?.disconnect();
              }}
            />
            <button className="btn btn-link px-2" type="button">
              <i className="bi bi-search"></i>
            </button>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-8 text-right">
          <button className="btn btn-primary btn-md position-relative" disabled={!selectedSkus.length} type="submit">
            {isAddingProduct ? (
              <span className="spinner-border spinner-border-sm p-2" role="status" aria-hidden="true"></span>
            ) : (
              "Add Items To List"
            )}
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-lg-4">
          {products.length > 0 && (
            <ProductList products={products} setSelectedSkus={setSelectedSkus} selectedSkus={selectedSkus} />
          )}

          {products.length === 0 && !isFetching && (
            <div className="card p-3 my-2">
              <h5 className="text-danger">No Data Found</h5>
            </div>
          )}

          {isFetching && (
            <div className="card p-3 my-2">
              <h5 className="text-center">Loading....</h5>
            </div>
          )}

          <div ref={loadMoreItemRef}>{/* blank div to tap when doing infinite scroll */}</div>
        </div>

        <div className="col-md-6 col-lg-8 table-responsive">
          <table className="w-100">
            <thead></thead>
            <tbody>
              {selectedSkus.map(({ productName, modifiers, skus }, parentIndex) => {
                if (!skus.length) {
                  return null;
                }
                return (
                  <Fragment key={parentIndex}>
                    <tr>
                      <td>
                        <h5 className="mx-1 my-2">{productName}</h5>
                      </td>
                    </tr>
                    {renderProductRow({
                      modifiers,
                      skus,
                      handleSelectedSkuRemove: (sku) => handleSelectedSkuRemove(productName, sku),
                      updateSelectSkuQuantity: (skus) => updateSelectSkuQuantity(productName, skus),
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </form>
  );
};

export default AddProductsTab;
